import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Sparkles, Mail } from "lucide-react";
import RoosterLogo from "@/components/RoosterLogo";
import EmailPreview from "@/components/EmailPreview";
import SignupSubscribeForm from "@/components/SignupSubscribeForm";
import { useAuth, hasEverAuthenticated } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REMEMBER_KEY = "weathervane:last-token";

export default function LandingPage() {
  const [previews, setPreviews] = useState(null); // [{label, forecast, sources_count}]
  const [previewing, setPreviewing] = useState(false);
  const [rememberedToken, setRememberedToken] = useState(null);
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const { search } = useLocation();

  // Auto-route returning users:
  //  - signed in   -> /app dashboard
  //  - has-account -> /login (session expired but they have an account)
  //  - first time  -> stay on the marketing landing page
  // Honors ?stay=1 so signed-out users can manually browse the landing
  // (e.g. clicking the Weathervane logo from /login).
  useEffect(() => {
    if (isLoading) return;
    const params = new URLSearchParams(search);
    if (params.get("stay") === "1") return;
    if (user) { navigate("/app", { replace: true }); return; }
    if (hasEverAuthenticated()) { navigate("/login", { replace: true }); }
  }, [isLoading, user, navigate, search]);

  useEffect(() => {
    try {
      const t = localStorage.getItem(REMEMBER_KEY);
      if (t) setRememberedToken(t);
    } catch {}
  }, []);

  const dismissRemembered = () => {
    try { localStorage.removeItem(REMEMBER_KEY); } catch {}
    setRememberedToken(null);
  };

  const handlePreview = async (locs) => {
    const valid = (Array.isArray(locs) ? locs : [locs]).filter((l) => l && l.lat && l.lon);
    if (!valid.length) {
      toast.error("Add a validated address first to see your forecast.");
      return;
    }
    setPreviewing(true);
    try {
      const results = await Promise.all(
        valid.map((l) =>
          axios
            .post(`${API}/forecast`, { lat: l.lat, lon: l.lon, location_label: l.label })
            .then((r) => ({ label: r.data.location_label || l.label, forecast: r.data.forecast, sources_count: r.data.sources_count }))
        )
      );
      setPreviews(results);
      const total = results[0]?.sources_count || 0;
      toast.success(
        results.length === 1
          ? `Forecast built from ${total} models.`
          : `Previewed ${results.length} locations from ${total} models each.`
      );
    } catch {
      toast.error("Could not fetch forecast right now.");
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <header className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <RoosterLogo size={51} />
            <div>
              <div className="font-display text-2xl font-black tracking-tighter leading-none">Weathervane</div>
              <div className="text-[8px] sm:text-[9px] uppercase tracking-[0.16em] sm:tracking-[0.18em] text-neutral-500 font-bold mt-1">Weather &middot; News &middot; Top 10 Stocks <span className="mx-1">/</span> <span className="italic">BYO Coffee</span></div>
            </div>
          </div>
          <div className="flex items-center gap-3 sm:gap-5">
            <a href="/news" data-testid="nav-news" className="text-[11px] uppercase tracking-widest font-semibold border-b-2 border-transparent hover:border-[#FFD600] pb-0.5">News</a>
            <a href="/app?tab=stocks" data-testid="nav-stocks" className="hidden sm:inline text-[11px] uppercase tracking-widest font-semibold border-b-2 border-transparent hover:border-[#FFD600] pb-0.5">Stocks</a>
            <div className="ticker-label hidden lg:block">AI weather, curated news, consensus stocks — daily</div>
            {user ? (
              <Link
                to="/app"
                data-testid="nav-dashboard"
                className="inline-flex items-center px-3 py-1.5 bg-black text-[#FFD600] hover:bg-[#FFD600] hover:text-black border border-black text-[10px] uppercase tracking-[0.16em] font-bold"
              >
                My dashboard
              </Link>
            ) : (
              <Link
                to="/login"
                data-testid="nav-signin"
                className="text-[11px] uppercase tracking-widest font-semibold border-b-2 border-transparent hover:border-[#FFD600] pb-0.5"
              >
                Sign in
              </Link>
            )}
          </div>
        </div>
      </header>

      <section className="relative">
        {rememberedToken && (
          <div className="bg-[#FFD600] border-b-2 border-black" data-testid="welcome-back-banner">
            <div className="max-w-7xl mx-auto px-6 lg:px-12 py-3 flex items-center justify-between gap-3">
              <div className="text-sm">
                <span className="font-display font-black tracking-tight">Welcome back. </span>
                <span className="text-neutral-800">Jump straight to your forecast &rarr;</span>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={`/today/${rememberedToken}`}
                  data-testid="welcome-back-cta"
                  className="btn-brutal inline-flex items-center px-3 py-2 text-[10px] tracking-[0.16em] uppercase font-semibold"
                >
                  Open my forecast
                </a>
                <button
                  onClick={dismissRemembered}
                  data-testid="welcome-back-dismiss"
                  aria-label="Dismiss"
                  className="text-[10px] tracking-[0.16em] uppercase font-semibold text-neutral-700 hover:text-black px-2 py-2"
                >
                  Not me
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="absolute inset-0 grid-overlay pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-4 pb-12 lg:pt-10 lg:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          {/* LEFT — copy + form */}
          <div className="lg:col-span-7">
            <div className="ticker-label mb-4">Accurate forecasts + targeted news.</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              The forecast,<br />
              news, and stocks<br />
              feed that{" "}
              <span className="relative inline-block">
                <span className="relative z-10">doesn&rsquo;t</span>
                <span
                  aria-hidden
                  className="absolute left-0 -bottom-1 h-3 w-full bg-[#FFD600] z-0"
                />
              </span>
              &nbsp;suck.
            </h1>

            {/* Punchy lede — the only paragraph above the fold. The
                rest of the value prop is delivered visually via the
                3-step "How it works" cards + topic chips below, so
                users see structure not a wall of text. */}
            <p
              className="mt-7 text-lg sm:text-xl text-neutral-800 max-w-xl leading-snug font-medium"
              data-testid="landing-lede"
            >
              A single daily brief: hyper-local weather, AI-curated news on the topics YOU pick, and a{" "}
              <span className="relative inline-block">
                <span className="relative z-10">Top&nbsp;10 Stocks</span>
                <span
                  aria-hidden
                  className="absolute left-0 -bottom-0.5 h-2 w-full bg-[#FFD600] z-0"
                />
              </span>
              {" "}consensus watch across Goldman, Morgan Stanley, JPMorgan and 9 other major desks.
            </p>

            {/* HOW IT WORKS — 3-step infographic. AI-POWERED pill
                next to the section label makes the AI value-prop
                front-and-center. The middle card (AI WORKS) gets a
                yellow brand background so the AI step is the
                visual anchor. Each card has a lucide icon for
                immediate recognition. */}
            <div className="mt-10 max-w-2xl" data-testid="landing-how-it-works">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <div className="ticker-label !text-[10px] text-neutral-700">
                  How it works
                </div>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-black text-[#FFD600] text-[10px] uppercase tracking-[0.18em] font-bold">
                  <Sparkles className="w-3 h-3" /> AI-Powered
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  {
                    step: "01",
                    title: "TELL US",
                    icon: MapPin,
                    body: "Locations you want forecasts for, and news topics you actually care about.",
                    accent: false,
                  },
                  {
                    step: "02",
                    title: "AI WORKS",
                    icon: Sparkles,
                    body: "6-model weather ensemble, live-web news search on your topics, and daily Top 10 Stocks consensus across 12 major research firms — synthesized by AI.",
                    accent: true,
                  },
                  {
                    step: "03",
                    title: "INBOX",
                    icon: Mail,
                    body: "A clean daily brief lands in your inbox at the time you choose. No ads. No bloat.",
                    accent: false,
                  },
                ].map((s) => {
                  const Icon = s.icon;
                  return (
                    <div
                      key={s.step}
                      className={`relative border-2 border-black p-4 hover:shadow-[4px_4px_0_#0A0A0A] transition-shadow ${
                        s.accent ? "bg-[#FFD600]" : "bg-white"
                      }`}
                      data-testid={`landing-step-${s.step}`}
                    >
                      <div className={`absolute -top-3 left-3 border-2 border-black px-2 py-0.5 font-display font-black text-xs tracking-tighter ${
                        s.accent ? "bg-white" : "bg-[#FFD600]"
                      }`}>
                        {s.step}
                      </div>
                      <div className="flex items-center gap-2 mt-2 mb-1.5">
                        <Icon className="w-4 h-4 stroke-[2.5]" />
                        <div className="font-display font-black text-base tracking-tight">
                          {s.title}
                        </div>
                      </div>
                      <div className={`text-[13px] leading-relaxed ${s.accent ? "text-black" : "text-neutral-700"}`}>
                        {s.body}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Six weather models — compact horizontal chip row.
                Reinforces the "6 models" claim without needing the
                user to scroll to the right column on mobile. */}
            <div className="mt-7 max-w-2xl">
              <div className="ticker-label !text-[10px] mb-2.5 text-neutral-600">
                Six weather models, ensembled
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="landing-models-row">
                {["ECMWF", "GFS", "ICON", "NAM", "Open-Meteo", "MET Norway"].map((m) => (
                  <span
                    key={m}
                    className="inline-flex items-center px-2.5 py-1 border border-black bg-white text-[11px] font-semibold tracking-tight"
                  >
                    {m}
                  </span>
                ))}
              </div>
            </div>

            {/* Example topics — quoted chips that show users the
                scope of "targeted news" with concrete examples. */}
            <div className="mt-6 max-w-2xl">
              <div className="ticker-label !text-[10px] mb-2.5 text-neutral-600">
                Targeted news, for example
              </div>
              <div className="flex flex-wrap gap-2" data-testid="landing-topic-examples">
                {[
                  "Major breakthroughs in molecular bioscience",
                  "Acquisitions in buy-side financial services",
                  "AI hardware launches",
                ].map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center px-3 py-1.5 bg-[#FFFBE6] border border-black text-[12px] italic"
                  >
                    &ldquo;{t}&rdquo;
                  </span>
                ))}
              </div>
              <p className="text-[12px] text-neutral-500 mt-3 leading-relaxed">
                Hyper-local weather. Highly specific news. Not a CNN/Fox News replacement &mdash; a targeted brief to prep you for the day.
              </p>
            </div>

            <SignupSubscribeForm
              onPreview={handlePreview}
              previewing={previewing}
            />
          </div>

          {/* RIGHT — rooster + preview */}
          <div className="lg:col-span-5">
            <div className="relative border border-black bg-white p-8 mb-6 overflow-hidden">
              <div className="absolute inset-0 diag-lines opacity-40" />
              <div className="relative flex items-start justify-between gap-4">
                <div>
                  <div className="ticker-label">Index</div>
                  <div className="font-display text-3xl font-black tracking-tighter mt-1">
                    6 models <br /> 1 AI-powered forecast
                  </div>
                </div>
                <RoosterLogo size={120} />
              </div>
              <div className="relative mt-6 grid grid-cols-3 gap-4 text-[11px]">
                {[
                  ["ECMWF", "Europe"],
                  ["GFS", "NOAA"],
                  ["ICON", "DWD"],
                  ["NAM", "NOAA"],
                  ["MET", "Norway"],
                  ["Open-Meteo", "Ensemble"],
                ].map(([a, b]) => (
                  <div key={a} className="border border-black/80 p-2">
                    <div className="font-semibold">{a}</div>
                    <div className="text-neutral-500">{b}</div>
                  </div>
                ))}
              </div>
            </div>
            <EmailPreview
              previews={previews}
              heading={previews ? "Live preview — 6 models" : "Sample email"}
            />
          </div>
        </div>
      </section>

      <footer className="border-t border-black/10 mt-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <RoosterLogo size={28} />
            <span className="font-display font-black">Weathervane</span>
          </div>
          <div className="flex items-center gap-5 text-[11px] uppercase tracking-widest font-semibold">
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-[#FFD600] hover:underline">Privacy</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-[#FFD600] hover:underline">Terms</Link>
          </div>
          <div className="ticker-label !normal-case !tracking-normal text-[11px] text-neutral-600 max-w-2xl sm:text-right leading-relaxed">
            © {new Date().getFullYear()} Weathervane, LLC. &ldquo;Weathervane&rdquo; and the Weathervane rooster design are registered trademarks of Weathervane, LLC. All Rights Reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
